import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GetRoutes, CreateRoute, UpdateRoute, DeleteRoute } from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME } from "../Constant/AppConstants";

export const routeQueryKeys = {
  routes: (params?: Parameters<typeof GetRoutes>[0]) =>
    ["routes", params] as const,
};

export const invalidateRouteQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["routes"] });
};

export const useRoutesQuery = (
  params: Parameters<typeof GetRoutes>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: routeQueryKeys.routes(params),
    queryFn: async () => unwrapApiResponse(await GetRoutes(params)),
  });
};

export const useCreateRouteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateRoute>[0]) =>
      CreateRoute(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRouteQueries(queryClient);
      }
    },
  });
};

export const useUpdateRouteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateRoute>[0]) =>
      UpdateRoute(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRouteQueries(queryClient);
      }
    },
  });
};

export const useDeleteRouteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteRoute>[0]) =>
      DeleteRoute(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateRouteQueries(queryClient);
      }
    },
  });
};

export const useRouteService = (config?: {
  fetchRoutesParams?: Parameters<typeof GetRoutes>[0];
  fetchRoutesEnabled?: boolean;
}) => {
  const routesQuery = useRoutesQuery(
    config?.fetchRoutesParams ?? { page: 1, limit: 100 },
    {
      enabled:
        config?.fetchRoutesEnabled !== false &&
        config?.fetchRoutesParams !== undefined &&
        config?.fetchRoutesParams !== null,
    },
  );

  const createRouteMutation = useCreateRouteMutation();
  const updateRouteMutation = useUpdateRouteMutation();
  const deleteRouteMutation = useDeleteRouteMutation();

  return {
    routesQuery,
    createRouteMutation,
    updateRouteMutation,
    deleteRouteMutation,
  };
};
